import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Button, Card, CardContent, Dialog,
  DialogContent, DialogActions, TextField, MenuItem, Chip, Stack,
  LinearProgress, IconButton, Tooltip, Tab, Tabs,
  FormControlLabel, Switch, Alert,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  CloudUpload as UploadIcon, Download as DownloadIcon,
  Verified as VerifiedIcon, Delete as DeleteIcon,
  Folder as FolderIcon, Warning as WarnIcon,
  Description as DocIcon, Shield as ComplianceIcon,
  AutoAwesome as AIDocIcon,
  Checklist as ChecklistIcon,
} from '@mui/icons-material';
import EmptyGridOverlay from '@/components/common/EmptyGridOverlay';
import Grid from '@mui/material/Grid2';
import BrandedDialogTitle from '@/components/common/BrandedDialogTitle';
import { useNotificationStore } from '@/stores/notificationStore';
import { usePermission } from '@/hooks/usePermission';
import PageHelpIcon from '@/components/PageHelpIcon';
import apiClient from '@/api/client';
import { TYPE } from '@/theme/typography';
import { STATUS_COLORS as TOKEN_STATUS_COLORS } from '@/theme/colorTokens';
import { usePaletteAccents } from '@/hooks/usePaletteAccents';
import DocIntelligencePanel from '@/components/hr/DocIntelligencePanel';

// ============================================================
// TYPES
// ============================================================
interface DocumentResponse {
  id: string;
  employee_id: string;
  company_id: string;
  title: string;
  category: string;
  description: string | null;
  file_name: string;
  file_size: number;
  mime_type: string;
  issue_date: string | null;
  expiry_date: string | null;
  is_expired: boolean;
  is_verified: boolean;
  verified_by: string | null;
  verified_at: string | null;
  is_confidential: boolean;
  created_at: string;
  employee_name: string | null;
  employee_ref: string | null;
}

interface DocumentRequirementResponse {
  id: string;
  company_id: string;
  title: string;
  category: string;
  description: string | null;
  department_id: string | null;
  designation: string | null;
  is_mandatory: boolean;
  has_expiry: boolean;
  is_active: boolean;
  created_at: string;
}

interface ComplianceItem {
  requirement_id: string;
  requirement_title: string;
  category: string;
  is_mandatory: boolean;
  status: string;
  affected_employees: { id: string; name: string; ref: string; detail: string }[];
  count: number;
}

interface ComplianceResponse {
  total_requirements: number;
  compliant_count: number;
  missing_count: number;
  expired_count: number;
  expiring_soon_count: number;
  items: ComplianceItem[];
}

const docsApi = {
  list: (params?: { employee_id?: string; category?: string }) =>
    apiClient.get<DocumentResponse[]>('/hr/documents', { params }),
  upload: (employeeId: string, formData: FormData) =>
    apiClient.post<DocumentResponse>(`/hr/documents/${employeeId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  verify: (docId: string) =>
    apiClient.post<DocumentResponse>(`/hr/documents/${docId}/verify`),
  delete: (docId: string) =>
    apiClient.delete(`/hr/documents/${docId}`),
  download: (docId: string) =>
    apiClient.get(`/hr/documents/${docId}/download`, { responseType: 'blob' }),
  // Requirements
  listReqs: () =>
    apiClient.get<DocumentRequirementResponse[]>('/hr/document-requirements'),
  createReq: (data: Record<string, unknown>) =>
    apiClient.post<DocumentRequirementResponse>('/hr/document-requirements', data),
  deleteReq: (id: string) =>
    apiClient.delete(`/hr/document-requirements/${id}`),
  // Compliance
  compliance: () =>
    apiClient.get<ComplianceResponse>('/hr/document-compliance'),
};

const CATEGORIES = [
  { value: 'identification', label: 'Identification', icon: '🪪' },
  { value: 'employment', label: 'Employment', icon: '📋' },
  { value: 'certification', label: 'Certification', icon: '📜' },
  { value: 'medical', label: 'Medical', icon: '🏥' },
  { value: 'education', label: 'Education', icon: '🎓' },
  { value: 'legal', label: 'Legal', icon: '⚖️' },
  { value: 'financial', label: 'Financial', icon: '💰' },
  { value: 'other', label: 'Other', icon: '📁' },
];

const STATUS_CHIP: Record<string, { color: 'success' | 'error' | 'warning' | 'default'; label: string }> = {
  compliant: { color: 'success', label: 'Compliant' },
  missing: { color: 'error', label: 'Missing' },
  expired: { color: 'error', label: 'Expired' },
  expiring_soon: { color: 'warning', label: 'Expiring Soon' },
};

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

// ============================================================
// UPLOAD DIALOG
// ============================================================
// Matches canonical UUID form — guards against an empty/typo employee id
// reaching the backend (which would 422 with a raw "Input should be a valid UUID").
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function UploadDialog({ open, onClose, selfEmployeeId }: { open: boolean; onClose: () => void; selfEmployeeId?: string }) {
  const qc = useQueryClient();
  const { showSuccess, showError } = useNotificationStore();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  // In self-service ("My Documents") mode the target is the signed-in user's own
  // record — the employee id is supplied by the caller, not typed by hand.
  const selfService = selfEmployeeId !== undefined;
  const [form, setForm] = useState({
    employee_id: selfEmployeeId ?? '',
    title: '',
    category: 'other',
    description: '',
    issue_date: '',
    expiry_date: '',
    is_confidential: false,
  });
  const set = (k: string, v: unknown) => setForm(prev => ({ ...prev, [k]: v }));
  const effectiveEmployeeId = (selfEmployeeId ?? form.employee_id).trim();
  const validEmployeeId = UUID_RE.test(effectiveEmployeeId);

  const uploadMut = useMutation({
    mutationFn: () => {
      if (!file) throw new Error('Choose a file to upload.');
      if (!validEmployeeId) {
        throw new Error(
          selfService
            ? 'Your account is not linked to an employee record yet, so documents can’t be uploaded. Ask an administrator to add you as an employee.'
            : 'Enter a valid Employee ID before uploading.',
        );
      }
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', form.title || file.name);
      fd.append('category', form.category);
      if (form.description) fd.append('description', form.description);
      if (form.issue_date) fd.append('issue_date', form.issue_date);
      if (form.expiry_date) fd.append('expiry_date', form.expiry_date);
      fd.append('is_confidential', String(form.is_confidential));
      return docsApi.upload(effectiveEmployeeId, fd);
    },
    onSuccess: () => {
      showSuccess('Document uploaded');
      qc.invalidateQueries({ queryKey: ['hr-documents'] });
      setFile(null);
      setForm({ employee_id: selfEmployeeId ?? '', title: '', category: 'other', description: '', issue_date: '', expiry_date: '', is_confidential: false });
      onClose();
    },
    // Client-side guards throw a plain Error (err.message); axios failures carry
    // the structured backend detail. Prefer whichever is present.
    onError: (err: any) => showError(err?.response?.data?.detail || err?.message || 'Upload failed'),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <BrandedDialogTitle>{selfService ? 'Upload My Document' : 'Upload Employee Document'}</BrandedDialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {selfService ? (
            !validEmployeeId && (
              <Alert severity="info">
                Your account is not linked to an employee record yet, so documents can’t be
                uploaded. Ask an administrator to add you as an employee.
              </Alert>
            )
          ) : (
            <TextField
              label="Employee ID" value={form.employee_id} onChange={e => set('employee_id', e.target.value)}
              fullWidth required size="small"
              error={!!form.employee_id && !validEmployeeId}
              helperText={!!form.employee_id && !validEmployeeId ? 'Must be a valid employee ID (UUID).' : ''}
            />
          )}
          <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => fileRef.current?.click()}>
            {file ? file.name : 'Choose File'}
          </Button>
          <input ref={fileRef} type="file" hidden onChange={e => setFile(e.target.files?.[0] || null)} accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx" />
          <TextField label="Title" value={form.title} onChange={e => set('title', e.target.value)} fullWidth size="small" placeholder={file?.name || ''} />
          <TextField select label="Category" value={form.category} onChange={e => set('category', e.target.value)} fullWidth size="small">
            {CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}>{c.icon} {c.label}</MenuItem>)}
          </TextField>
          <TextField label="Description" value={form.description} onChange={e => set('description', e.target.value)} fullWidth size="small" multiline rows={2} />
          <Grid container spacing={2}>
            <Grid size={6}>
              <TextField label="Issue Date" type="date" value={form.issue_date} onChange={e => set('issue_date', e.target.value)} fullWidth size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={6}>
              <TextField label="Expiry Date" type="date" value={form.expiry_date} onChange={e => set('expiry_date', e.target.value)} fullWidth size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => uploadMut.mutate()} disabled={uploadMut.isPending || !file || !validEmployeeId}>Upload</Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================
// DOCUMENTS TAB
// ============================================================
function DocumentsTab({ selfEmployeeId }: { selfEmployeeId?: string }) {
  const { hasPermission } = usePermission();
  // In self-service mode the user may upload their own document even without the
  // admin-level hr.write permission.
  const selfService = selfEmployeeId !== undefined;
  const canWrite = hasPermission('hr.write') || selfService;
  const [uploadOpen, setUploadOpen] = useState(false);
  const [catFilter, setCatFilter] = useState('');
  const qc = useQueryClient();
  const { showSuccess, showError } = useNotificationStore();

  const { data: docs = [], isLoading } = useQuery({
    queryKey: ['hr-documents', catFilter],
    queryFn: () => docsApi.list(catFilter ? { category: catFilter } : undefined),
    select: r => r.data,
  });

  const verifyMut = useMutation({
    mutationFn: (id: string) => docsApi.verify(id),
    onSuccess: () => { showSuccess('Document verified'); qc.invalidateQueries({ queryKey: ['hr-documents'] }); },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => docsApi.delete(id),
    onSuccess: () => { showSuccess('Document deleted'); qc.invalidateQueries({ queryKey: ['hr-documents'] }); },
  });

  const handleDownload = async (doc: DocumentResponse) => {
    try {
      const res = await docsApi.download(doc.id);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name;
      a.click();
      URL.revokeObjectURL(url);
    } catch { showError('Download failed'); }
  };

  const columns: GridColDef[] = [
    { field: 'employee_name', headerName: 'Employee', flex: 1, minWidth: 150,
      renderCell: p => (
        <Box>
          <Typography variant="body2" fontWeight={TYPE.semibold}>{p.value}</Typography>
          <Typography variant="caption" color="text.secondary">{p.row.employee_ref}</Typography>
        </Box>
      ),
    },
    { field: 'title', headerName: 'Document', flex: 1, minWidth: 180,
      renderCell: p => (
        <Stack direction="row" spacing={1} alignItems="center">
          <DocIcon fontSize="small" color="action" />
          <Box>
            <Typography variant="body2">{p.value}</Typography>
            <Typography variant="caption" color="text.secondary">{formatSize(p.row.file_size)}</Typography>
          </Box>
        </Stack>
      ),
    },
    { field: 'category', headerName: 'Category', flex: 0.5, minWidth: 90,
      renderCell: p => {
        const cat = CATEGORIES.find(c => c.value === p.value);
        return <Chip label={cat?.label || p.value} size="small" variant="outlined" />;
      },
    },
    { field: 'expiry_date', headerName: 'Expires', flex: 0.5, minWidth: 90,
      renderCell: p => {
        if (!p.value) return '—';
        const isExpired = new Date(p.value) < new Date();
        return (
          <Typography variant="body2" color={isExpired ? 'error' : 'text.primary'}>
            {p.value} {isExpired && <WarnIcon fontSize="inherit" color="error" />}
          </Typography>
        );
      },
    },
    { field: 'is_verified', headerName: 'Verified', flex: 0.3, minWidth: 70, align: 'center',
      renderCell: p => p.value ? <VerifiedIcon color="success" fontSize="small" /> : <Chip label="No" size="small" variant="outlined" />,
    },
    { field: 'is_confidential', headerName: 'Conf.', width: 60, align: 'center',
      renderCell: p => p.value ? <Chip label="Yes" size="small" color="warning" /> : null,
    },
    { field: 'actions', headerName: '', width: 110, sortable: false,
      renderCell: p => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Download"><IconButton size="small" onClick={() => handleDownload(p.row)}><DownloadIcon fontSize="small" /></IconButton></Tooltip>
          {canWrite && !p.row.is_verified && (
            <Tooltip title="Verify"><IconButton size="small" onClick={() => verifyMut.mutate(p.row.id)}><VerifiedIcon fontSize="small" /></IconButton></Tooltip>
          )}
          {canWrite && (
            <Tooltip title="Delete"><IconButton size="small" onClick={() => deleteMut.mutate(p.row.id)}><DeleteIcon fontSize="small" color="error" /></IconButton></Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <TextField select label="Category" value={catFilter} onChange={e => setCatFilter(e.target.value)} size="small" sx={{ minWidth: 150 }}>
          <MenuItem value="">All Categories</MenuItem>
          {CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
        </TextField>
        {canWrite && (
          <Button variant="contained" startIcon={<UploadIcon />} onClick={() => setUploadOpen(true)}>Upload</Button>
        )}
      </Stack>
      <Card>
        <DataGrid
          autoHeight
          density="compact"
          rows={docs}
          columns={columns}
          loading={isLoading}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          getRowId={r => r.id}
          sx={{ border: 0 }}
          slots={{
            noRowsOverlay: () => (
              <EmptyGridOverlay
                icon={<DocIcon sx={{ fontSize: 40, color: 'primary.main' }} />}
                title="No documents"
                description="Upload employee documents to keep records organized."
                actionLabel={canWrite ? 'Upload Document' : undefined}
                onAction={canWrite ? () => setUploadOpen(true) : undefined}
              />
            ),
          }}
        />
      </Card>
      {uploadOpen && <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} selfEmployeeId={selfEmployeeId} />}
    </Box>
  );
}

// ============================================================
// COMPLIANCE TAB
// ============================================================
function ComplianceTab() {
  const ACCENT = usePaletteAccents();
  const { data, isLoading } = useQuery({
    queryKey: ['doc-compliance'],
    queryFn: () => docsApi.compliance(),
    select: r => r.data,
  });

  if (isLoading) return <LinearProgress />;
  if (!data) return null;

  const kpis = [
    { label: 'Requirements', value: data.total_requirements, color: ACCENT.blue },
    { label: 'Compliant', value: data.compliant_count, color: ACCENT.green },
    { label: 'Missing', value: data.missing_count, color: ACCENT.red },
    { label: 'Expired', value: data.expired_count, color: ACCENT.orange },
    { label: 'Expiring Soon', value: data.expiring_soon_count, color: TOKEN_STATUS_COLORS.warning },
  ];

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpis.map(k => (
          <Grid key={k.label} size={{ xs: 6, sm: 4, md: 2.4 }}>
            <Card sx={{ borderTop: `3px solid ${k.color}` }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">{k.label}</Typography>
                <Typography variant="h5" fontWeight={TYPE.bold}>{k.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Stack spacing={2}>
        {data.items.map(item => {
          const chip = STATUS_CHIP[item.status] || { color: 'default' as const, label: item.status };
          return (
            <Card key={item.requirement_id} variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="subtitle2" fontWeight={TYPE.bold}>{item.requirement_title}</Typography>
                      <Chip label={item.category} size="small" variant="outlined" />
                      {item.is_mandatory && <Chip label="Required" size="small" color="error" variant="outlined" />}
                    </Stack>
                    {item.count > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {item.affected_employees.map(e => `${e.name} (${e.detail})`).join(', ')}
                      </Typography>
                    )}
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label={`${item.count} affected`} size="small" />
                    <Chip label={chip.label} size="small" color={chip.color} />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
}

// ============================================================
// REQUIREMENTS TAB
// ============================================================
function RequirementsTab() {
  const { hasPermission } = usePermission();
  const canWrite = hasPermission('settings.company.write');
  const qc = useQueryClient();
  const { showSuccess } = useNotificationStore();
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ title: '', category: 'identification', description: '', is_mandatory: true, has_expiry: false });

  const { data: reqs = [], isLoading } = useQuery({
    queryKey: ['doc-requirements'],
    queryFn: () => docsApi.listReqs(),
    select: r => r.data,
  });

  const defaultForm = { title: '', category: 'identification', description: '', is_mandatory: true, has_expiry: false };
  const createMut = useMutation({
    mutationFn: () => docsApi.createReq(form),
    onSuccess: () => { showSuccess('Requirement added'); qc.invalidateQueries({ queryKey: ['doc-requirements'] }); setAddOpen(false); setForm(defaultForm); },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => docsApi.deleteReq(id),
    onSuccess: () => { showSuccess('Requirement removed'); qc.invalidateQueries({ queryKey: ['doc-requirements'] }); },
  });

  const columns: GridColDef[] = [
    { field: 'title', headerName: 'Document', flex: 1, minWidth: 120 },
    { field: 'category', headerName: 'Category', flex: 0.5, minWidth: 90 },
    { field: 'is_mandatory', headerName: 'Mandatory', flex: 0.4, minWidth: 80, renderCell: p => p.value ? 'Yes' : 'No' },
    { field: 'has_expiry', headerName: 'Tracks Expiry', flex: 0.4, minWidth: 80, renderCell: p => p.value ? 'Yes' : 'No' },
    { field: 'actions', headerName: '', width: 60, sortable: false,
      renderCell: p => canWrite ? (
        <IconButton size="small" onClick={() => deleteMut.mutate(p.row.id)}><DeleteIcon fontSize="small" color="error" /></IconButton>
      ) : null,
    },
  ];

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        {canWrite && <Button variant="contained" onClick={() => setAddOpen(true)}>Add Requirement</Button>}
      </Stack>
      <Card>
        <DataGrid
          autoHeight
          density="compact"
          rows={reqs}
          columns={columns}
          loading={isLoading}
          disableRowSelectionOnClick
          getRowId={r => r.id}
          sx={{ border: 0 }}
          slots={{
            noRowsOverlay: () => (
              <EmptyGridOverlay
                icon={<ChecklistIcon sx={{ fontSize: 40, color: 'primary.main' }} />}
                title="No compliance requirements"
                description="Add compliance requirements to track mandatory employee documents."
                actionLabel={canWrite ? 'Add Requirement' : undefined}
                onAction={canWrite ? () => setAddOpen(true) : undefined}
              />
            ),
          }}
        />
      </Card>
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <BrandedDialogTitle>Add Document Requirement</BrandedDialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Title" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} fullWidth size="small" />
            <TextField select label="Category" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} fullWidth size="small">
              {CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
            </TextField>
            <TextField label="Description" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} fullWidth size="small" multiline rows={2} />
            <FormControlLabel control={<Switch checked={form.is_mandatory} onChange={e => setForm(p => ({ ...p, is_mandatory: e.target.checked }))} />} label="Mandatory" />
            <FormControlLabel control={<Switch checked={form.has_expiry} onChange={e => setForm(p => ({ ...p, has_expiry: e.target.checked }))} />} label="Track Expiry" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => createMut.mutate()}>Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ============================================================
// MAIN PAGE
// ============================================================
export default function DocumentsPage({ embedded, selfEmployeeId }: { embedded?: boolean; selfEmployeeId?: string } = {}) {
  const [tab, setTab] = useState(0);
  const { hasPermission } = usePermission();
  const isAdmin = hasPermission('hr.write');

  return (
    <Box>
      {!embedded && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <FolderIcon color="primary" />
          <Typography variant="h5" fontWeight={TYPE.bold}>Employee Documents</Typography>
          <PageHelpIcon pageKey="employee-documents" />
        </Stack>
      )}

      <Box sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label={embedded ? 'My Documents' : 'All Documents'} />
          {isAdmin && <Tab label="Compliance Check" icon={<ComplianceIcon />} iconPosition="start" />}
          {isAdmin && <Tab label="Requirements" />}
          {isAdmin && <Tab label="Doc Intelligence" icon={<AIDocIcon />} iconPosition="start" />}
        </Tabs>
      </Box>

      {tab === 0 && <DocumentsTab selfEmployeeId={embedded ? selfEmployeeId : undefined} />}
      {tab === 1 && isAdmin && <ComplianceTab />}
      {tab === 2 && isAdmin && <RequirementsTab />}
      {tab === 3 && isAdmin && <DocIntelligencePanel />}
    </Box>
  );
}
